import { Deserializer } from "jsonapi-serializer";
import { ROLES } from "../constants/preset-constants";
import { ReportTypes, getMusterReportStatus } from "../constants/report-types";
import { INTERNAL_SERVER_ERROR, NO_CONTENT } from "../constants/response-status-codes";
import { TranslationKeys } from "../contexts/TranslationKeys";
import { Translator } from "../contexts/translator";
import { AbsenteeismReport, AbsenteeismReportWrapper } from "../models/AbsenteeismReport";
import { AccessGroupReport, AccessGroupReportWrapper } from "../models/AccessGroupReport";
import { AreaReport, AreaReportWrapper } from "../models/AreaReport";
import { ExpiredCredentialReport, ExpiredCredentialWrapper } from "../models/ExpiredCredentialReport";
import { Filter } from "../models/Filter";
import { FirstInLastOutReport } from "../models/FirstInLastOutReport";
import { genericError } from "../models/GenericError";
import { HardwareInstallationReport, HardwareReportWrapper } from "../models/HardwareInstallationReport";
import { HoursWorkedReport, HoursWorkedReportkWrapper } from "../models/HoursWorkedReport";
import { ListQuery } from "../models/ListQuery";
import { MusteringReport, MusteringReportReportWrapper } from "../models/MusteringReport";
import { MusteringReportTotals } from "../models/MusteringReportTotals";
import { Name } from "../models/Name";
import { PaginationModel } from "../models/Pagination";
import { PersonImage } from "../models/Person";
import { ToastFunction } from "../models/ReactToastifyType";
import { Report, ReportWrapper } from "../models/Report";
import { StillOnSite, StillOnSiteWrapper } from "../models/StillOnSite";
import { TnaTransactionReport, TnaTransactionReportWrapper } from "../models/TnaTransactionReport";
import { TransactionReport, TransactionReportWrapper } from "../models/TransactionReport";
import { VingcardTransactionReport, VingcardTransactionReportWrapper } from "../models/VingcardTransactionReport";
import { ZoneOccupancy, ZoneOccupancykWrapper } from "../models/ZoneOccupancy";
import { SearchTerm } from "../models/search/SearchTerm";
import { StoreEntityResult } from "../models/services/StoreEntityResult";
import { buildFirstInLastOutWeekDays, convertDatesToWeekDays, getAllCurrentWeekDates, getTodayDate } from "../utilities/DateUtils";
import { showTagCode } from "../utilities/Permissions";
import { buildHoursWorkedDetails } from "../utilities/reportUtil";
import { APIConnector } from "./api/APIConnector";
import { getConstraintViolation, processServerError } from "./utilities/ServiceError";
import { getReportStatus } from "./utilities/StatusBuilder";
import { convertToDuration, convertToTime, getFormattedDate, getFormattedDateTime, getFormattedTime, getFormattedTimeFromNum, getTime, getUnFormattedDate } from "./utilities/dateFormatting";
import { createPagination } from "./utilities/paginationBuilder";
import { createPartialURL, getFilterClause } from "./utilities/urlBuilder";

const apiConnector = new APIConnector();

const buildTransaction = (transaction, profileId, translate: Translator): TransactionReport => {
    return {
        id: transaction["id"],
        name: transaction["master-by-tr-master-id"]
            ? transaction["master-by-tr-master-id"]["mst-displayname"]
            : transaction["tr-master-mst-displayname"],
        dateTime: transaction["tr-datetimelocal"]
            .replace("T", " ")
            .replace(/\.\d+$/, "")
            .replaceAll("-", "/"),
        eventType: transaction["event-type"]["trn-name"],
        transactionType: transaction["transack-type"]["trn-name"],
        masterController: transaction["tr-ctrl-name"],
        reader: 
            transaction["tr-ctrl-name"] === transaction["tr-dev-name"]
                ? ""
                : transaction["tr-dev-name"],
        building: transaction["tr-building-name"],
        floor: transaction["tr-floor-name"],
        zone: transaction["tr-zone-name"],
        site: transaction["tr-site-name"],
        tagCode: showTagCode(profileId, transaction["tr-tagcode"]),
        fixedAddress: transaction["tr-dev-fixedaddr"],
        logicalAddress: transaction["tr-sla"],
        serialAddress: transaction["tr-dev-serial"],
        company: transaction["tr-comp-name"],
        door: transaction["tr-loc-name"],
        department: transaction["tr-dept-name"],
        firstName: transaction["master-by-tr-master-id"]
            ? transaction["master-by-tr-master-id"]["mst-firstname"]
            : "",
        gender: transaction["master-by-tr-master-id"]
            ? getGenderLabel(transaction["master-by-tr-master-id"]["mst-gender"], translate)
            : "",
        lastName: transaction["master-by-tr-master-id"]
            ? transaction["master-by-tr-master-id"]["mst-lastname"]
            : "",
        middleName: transaction["master-by-tr-master-id"]
            ? transaction["master-by-tr-master-id"]["mst-middlename"]
            : "",
        title: transaction["master-by-tr-master-id"]
            ? transaction["master-by-tr-master-id"]["mst-title"]
            : "",
        category: transaction["master-by-tr-master-id"]?.["profile"]
            ? transaction["master-by-tr-master-id"]["profile"]["trn-name"]
            : "",
        reasonCode: transaction["tr-reason-code-name"],
        deviceType: transaction["unit-type"]["trn-name"],
        direction: transaction["tr-direction"] === 0 ? "Entry" : "Exit",
        access: transaction["master-by-tr-master-id"]?.["master-type"]
            ? transaction["master-by-tr-master-id"]["master-type"]["trn-name"]
            : "",
        dateOnly: transaction["date-only"]
    };
};

export const getTransactionReport = async (profileId: number, translate: Translator, listParameters?: ListQuery): Promise<TransactionReportWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm, 'trDatetimelocal');
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        let includes = "include=eventType,masterByTrMasterId.profile,";
        includes += "masterByTrMasterId.masterType,transackType,company,department,unitType,location";
        let transactionFields = "fields[transack]=masterByTrMasterId,trDatetimelocal,";
        transactionFields += "eventType,transackType,trCtrlName,trDevName,trBuildingName,trFloorName,";
        transactionFields += "trZoneName,trSiteName,trTagcode,trDevFixedaddr,trSla,trDevSerial,"
        transactionFields += "trCompName,trLocName,trDeptName,trReasoncode,unitType,trDirection,dateOnly,trReasonCodeName,trMasterMstDisplayname";
        const profileFields = "fields[profile]=trnName";
        const eventTypeFields = "fields[eventType]=trnName";
        const transactionTypeFields = "fields[transackType]=trnName";
        const unitTypeFields = "fields[unitType]=trnName";
        const masterFields = "fields[master]=masterType,mstDisplayname,mstFirstname,mstGender,mstIdnumber,mstLastname,mstMiddlename,mstTitle,mstType,profile";
        const locationFields = "fields[location]=locName";
        const masterTypeFields = "fields[masterType]=trnName";
        const sortOrder = "sort=-trDatetimelocal";
        const url = `transack?${includes}&${transactionFields}&${eventTypeFields}&${transactionTypeFields}&${unitTypeFields}&${masterFields}&${masterTypeFields}&${locationFields}&${profileFields}${partialURL}&${sortOrder}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }
            new Deserializer().deserialize(json, (error: any, data: any[]) => {
                    if (error) {
                        return reject({ status: response.status, message: genericError });
                    }

                    const transactions: TransactionReport[] = data.map((data) => buildTransaction(data, profileId, translate));

                    return resolve({ rows: transactions, pagination });
                }
            );
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const getUserTransactionReport = async (profileId: number, masterId: number, translate: Translator, listParameters?: ListQuery): Promise<TransactionReportWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm, 'trDatetimelocal');
        const withAdditionalFilter = `${filter ? filter.replace('filter=', `filter=masterByTrMasterId.id==${masterId};`) : `&filter=masterByTrMasterId.id==${masterId}`}`;
        const paginationString = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, withAdditionalFilter);
        const includes = "include=eventType,masterByTrMasterId.profile,masterByTrMasterId.masterType,transackType,company,department,unitType,location";
        let transactionFields = "fields[transack]=masterByTrMasterId,trDatetimelocal,trCtrlName,trDevName,";
        transactionFields += "trBuildingName,trFloorName,trZoneName,trSiteName,trTagcode,trDevFixedaddr,trSla,trMasterMstDisplayname,";
        transactionFields += "trDevSerial,trCompName,trLocName,trDeptName,trReasoncode,eventType,transackType,unitType,trDirection,dateOnly,trReasonCodeName";
        const eventTypeFields = "fields[eventType]=trnName";
        const transackTypeFields = "fields[transackType]=trnName";
        const unitTypeFields = "fields[unitType]=trnName";
        const sortOrder = "sort=-trDatetimelocal";
        const url = `transack?${includes}&${transactionFields}&${eventTypeFields}&${transackTypeFields}&${unitTypeFields}${paginationString}&${sortOrder}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }
            new Deserializer().deserialize(
                json,
                (error: any, data: any[]) => {
                    if (error) {
                        return reject({ status: response.status, message: genericError });
                    }

                    const transactions: TransactionReport[] = data.map((transaction) => buildTransaction(transaction, profileId, translate));

                    return resolve({ rows: transactions, pagination });
                }
            );
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const getTimeAttendanceTransactionReport = async (listParameters?: ListQuery): Promise<TnaTransactionReportWrapper> => {
    try {
        const newFilters: Filter[] = [];

        listParameters?.filters?.forEach((filter) => {
            if (filter.key === 'dateOnly') {
                const selected = filter.selected.map((selected) => {
                    return {
                        ...selected,
                        value: getUnFormattedDate(selected.value)
                    }
                });

                newFilters.push({ ...filter, selected });
                return;
            }

            newFilters.push(filter);
        })

        const filter: string = getFilterClause(
            newFilters,
            listParameters?.searchTerm
        );
        const partialURL = createPartialURL(
            listParameters?.goToPage,
            listParameters?.pageSize,
            listParameters?.sortBy,
            filter
        );

        const url = `bioTransack?include=bioType&fields[bioTransack]=btrCol1,btrSerial,btrTime,dateOnly,btrType,btrKeyName,btrDisplayname,btrFirstname,btrLastname,btrIdnumber,bioType&fields[bioType]=trnName,bioMake${partialURL}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }
            new Deserializer().deserialize(
                json,
                (error: any, data: any[]) => {
                    if (error) {
                        return reject({ status: response.status, message: genericError });
                    }

                    const transactions: TnaTransactionReport[] = data.map((report) => {
                        return {
                            id: report["id"],
                            name: "",
                            userIdCsn: report["btr-col1"],
                            serialAddress: report["btr-serial"],
                            time: getFormattedTimeFromNum(report["btr-time"]),
                            dateOnly: getFormattedDate(report["date-only"]),
                            button: report["btr-type"],
                            buttonName: report["btr-key-name"],
                            biometricTypeId: report["bio-type"]["id"],
                            biometricType: report["bio-type"]["trn-name"],
                            biometricMake: report["bio-type"]["bio-make"],
                            displayName: report["btr-displayname"],
                            firstName: report["btr-firstname"],
                            lastName: report["btr-lastname"],
                            idNumber: report["btr-idnumber"],
                        };
                    });
                    return resolve({ rows: transactions, pagination });
                }
            );
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const getAllReport = async (listParameters?: ListQuery, userProfileId?: number): Promise<ReportWrapper> => {
    try {
        const defaultSortBy = "rptSysname,reportType.trnName,trnName";
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter, defaultSortBy);
        const includes = "include=reportType,userLogin.master,userLogin.site";
        const reportTypes = "fields[reportType]=trnName";
        const reportFields = "fields[report]=trnName,rptDesc,rptSysname,reportType,userLogin";
        const userLoginFields = "fields[userLogin]=ulUsername,master,site";
        const siteFields = "fields[site]=siteName";
        const masterFields = "fields[master]=mstDisplayname"
        let url = `report?${includes}&${reportTypes}&${reportFields}&${userLoginFields}&${siteFields}&${masterFields}${partialURL}`;

        // Limit reports available for certain roles
        if ((userProfileId !== ROLES.ID_SYSTEM_OWNER)
            && (userProfileId !== ROLES.ID_SITE_ADMIN)
            && (userProfileId !== ROLES.ID_INSTALLER)
            && (userProfileId !== ROLES.ID_REPORTS)) {
            // hide general mustering report from unsupported roles
            const filterOutReport = `reportType.id=out=(${ReportTypes.MUSTERING})`;
            if (url.includes('&filter')) {
                url = url + ';' + filterOutReport;
            }
            else {
                url = url + '&filter=' + filterOutReport;
            }
        }

        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }
            new Deserializer().deserialize(
                json,
                (error: any, data: any[]) => {
                    if (error) {
                        return reject({ status: response.status, message: genericError });
                    }

                    const reports: Report[] = data.map((report) => {
                        return {
                            id: report["id"],
                            name: report["trn-name"],
                            description: report["rpt-desc"],
                            type: report["report-type"]["trn-name"],
                            createdBy: report["user-login"]["master"] ? report["user-login"]["master"]["mst-displayname"] : report["user-login"]["ul-username"],
                            siteId: report["user-login"]["site"]["id"],
                            siteName: report["user-login"]["site"]["site-name"],
                            status: getReportStatus(report["rpt-sysname"]),
                            typeId: report["report-type"]["id"],
                            nameOriginal: report["trn-name"],
                        };
                    });
                    return resolve({ rows: reports, pagination });
                }
            );
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject({ message: genericError })
        });
    }
};

export const getAllTransactionReports = async (listParameters?: ListQuery): Promise<ReportWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const includes = "include=reportType,userLogin.master,userLogin.site";
        const reportFields = "fields[report]=trnName,rptDesc,reportType,userLogin,rptSysname";
        const reportTypeFields = "fields[reportType]=trnName";
        const userLoginFields = "fields[userLogin]=master,ulUsername,site";
        const masterFields = "fields[master]=mstDisplayname";
        const siteFields = "fields[site]=siteName";
        let url = `report?${includes}&${reportFields}&${reportTypeFields}&` +
            `${userLoginFields}&${masterFields}&${siteFields}${partialURL}`;
        const filterIn = 'reportType.id=in=(1)&sort=rptSysname,reportType.trnName,trnName';
        if (url.includes('&filter')){
            url = url + ';' + filterIn;
        }
        else {
            url = url + '&filter=' + filterIn;
        }

        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ statusCode: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }
            new Deserializer().deserialize(
                json,
                (error: any, data: any[]) => {
                    if (error) {
                        return reject({ statusCode: response.status, message: genericError });
                    }

                    const reports: Report[] = data.map((report) => {
                        return {
                            id: report["id"],
                            name: report["trn-name"],
                            description: report["rpt-desc"],
                            type: report["report-type"]["trn-name"],
                            createdBy: report["user-login"]["master"]
                                ? report["user-login"]["master"][
                                "mst-displayname"
                                ]
                                : report["user-login"]["ul-username"],
                            siteId: report["user-login"]["site"]["id"],
                            siteName: report["user-login"]["site"]["site-name"],
                            status: getReportStatus(report["rpt-sysname"]),
                            typeId: report["report-type"]["id"],
                        };
                    });
                    return resolve({ rows: reports, pagination });
                }
            );
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject({ message: genericError })
        });
    }
};

export const getAllReportTypeNamesWithPagination = async (
    listParameters?: ListQuery
): Promise<{ rows: Name[]; pagination: PaginationModel }> => {
    try {
        const filter: string = getFilterClause(
            listParameters?.filters,
            listParameters?.searchTerm
        );
        const partialURL = createPartialURL(
            listParameters?.goToPage,
            listParameters?.pageSize,
            listParameters?.sortBy,
            filter
        );
        const url = `reportType?${partialURL}`;

        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }
            new Deserializer().deserialize(json, (err: any, items: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }

                const reportTypes: Name[] = items.map(
                    (reportType): Name => {
                        return {
                            value: reportType["id"],
                            label: reportType["trn-name"],
                        };
                    }
                );
                return resolve({ rows: reportTypes, pagination });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const deleteReport = async (
    id: string
): Promise<{ id: string; success: boolean }> => {
    try {
        const url = `report/${id}`;
        const reportType = {
            data: {
                type: "report",
                id: id,
            },
        };
        const response = await apiConnector.deleteData(url, reportType);

        return new Promise(async (resolve, reject) => {
            if (response.status === NO_CONTENT) {
                return resolve({ id: id, success: true });
            }

            const json = await response.json();

            return reject({ id: id, success: false, status: response.status, message: getConstraintViolation(json.errors[0].detail) });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const storeReport = async (
    report: Report,
    userId: string,
    createReport = true
): Promise<StoreEntityResult> => {
    try {
        let reportSubType;
        if (report.reportSubTypeId) {
            reportSubType = {
                reportSubType: {
                    data: {
                        type: "reportSubType",
                        id: report.reportSubTypeId,
                    },
                },
            }
        }

        const newRecord = {
            data: {
                type: "report",
                id: report.id,
                attributes: {
                    trnName: report.name,
                    rptDesc: report.description,
                    rptHidden: 0,
                    rptSysname: "USER_DEFINED",
                },
                relationships: {
                    reportType: {
                        data: {
                            type: "reportType",
                            id: report.typeId,
                        },
                    },
                    ...reportSubType,
                    userLogin: {
                        data: {
                            type: "userLogin",
                            id: report.userLoginId ? report.userLoginId : userId,
                        },
                    },
                },
            },
        };

        const url = `report${createReport ? "" : `/${newRecord.data.id}`}`;
        let response: Response;

        await apiConnector
            .sendData(url, createReport, newRecord)
            .then((data) => {
                response = data;
            })
            .catch((error) => {
                return new Promise((resolve, reject) => {
                    return reject(processServerError(error));
                })
            });

        return new Promise<StoreEntityResult>(async (resolve, reject) => {
            if (response) {
                if (createReport) {
                    const content = await response.json();

                    if (content.errors) {
                        return reject({ status: response.status, message: getConstraintViolation(content.errors[0].detail), success: false })
                    }
                    new Deserializer().deserialize(content, (error, report) => {
                        if (error) {
                            return reject({ status: response.status, id: report.id, success: false });
                        }
                        return resolve({ id: report.id, success: true });
                    })

                    return;
                }
                if (response.status === NO_CONTENT) {
                    return resolve({ id: report.id, success: true })
                }

                const content = await response.json();
                return reject({ status: response.status, id: report.id, success: false, message: content.errors.map(error => getConstraintViolation(error.detail)) });
            }
            return reject({ status: INTERNAL_SERVER_ERROR, id: report.id, success: false, message: genericError });

        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const getAllReportNamesWithPagination = async (
    listParameters?: ListQuery,
    userProfileId?: number,
): Promise<{ rows: Name[]; pagination: PaginationModel }> => {
    try {
        const filter: string = getFilterClause(
            listParameters?.filters,
            listParameters?.searchTerm
        );
        const partialURL = createPartialURL(
            listParameters?.goToPage,
            listParameters?.pageSize,
            listParameters?.sortBy,
            filter
        );

        let url = `report?sort=rptSysname,trnName${partialURL}`;

        if ((userProfileId !== ROLES.ID_SYSTEM_OWNER) && (userProfileId !== ROLES.ID_SITE_ADMIN) && (userProfileId !== ROLES.ID_INSTALLER)) {
            // hide general mustering report from unsupported roles
            const filterOutReport = `reportType.id=out=(${ReportTypes.MUSTERING})`;
            if (url.includes('&filter')) {
                url = url + ';' + filterOutReport;
            }
            else {
                url = url + '&filter=' + filterOutReport;
            }
        }

        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }
            new Deserializer().deserialize(
                json,
                (error: any, data: any[]) => {
                    if (error) {
                        return reject({ status: response.status, message: genericError });
                    }

                    const reportNames: Name[] = data.map((reportName) => {
                        return {
                            value: reportName["id"],
                            label: reportName["trn-name"],
                        };
                    });

                    return resolve({
                        rows: reportNames,
                        pagination: pagination,
                    });
                }
            );
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const getReportById = async (id: string): Promise<Report> => {
    try {
        const reportFields = "fields[report]=trnName,rptDesc,reportType,userLogin,rptSysname,rptExecDatetime,reportSubType";
        const userLoginFields = "fields[userLogin]=master,ulUsername,site";
        const siteFields = "fields[site]=siteName";
        const masterFields = "fields[master]=mstDisplayname";
        const reportTypeFields = "fields[reportType]=trnName";
        const url = `report/${id}?include=reportType,userLogin.master,userLogin.site,reportSubType&${reportFields}&${userLoginFields}&${siteFields}&${masterFields}&${reportTypeFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }
            new Deserializer().deserialize(
                json,
                (error: any, data: any[]) => {
                    if (error) {
                        return reject({ status: response.status, message: genericError });
                    }

                    if (data['id']) {
                        const report: Report = {
                            id: data["id"],
                            name: data["trn-name"],
                            description: data["rpt-desc"],
                            type: data["report-type"]["trn-name"],
                            createdBy: data["user-login"]["master"] ? data["user-login"]["master"]["mst-displayname"] : data["user-login"]["ul-username"],
                            siteId: data["user-login"]["site"]["id"],
                            siteName: data["user-login"]["site"]["site-name"],
                            typeId: data["report-type"]["id"],
                            isTemplate: data["rpt-sysname"] === "TEMPLATE" ? true : false,
                            reportSubTypeId: data["report-sub-type"] ? data["report-sub-type"]["id"] : "",
                            lastExecutedTime: data["rpt-exec-datetime"] ? data["rpt-exec-datetime"] : "",
                            userLoginId: data["user-login"]["id"],
                        };

                        return resolve(report);
                    }
                    else {
                        reject(processServerError(error));
                    }
                }
            );
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const getAccessGroupReport = async (translate?: Translator, listParameters?: ListQuery): Promise<AccessGroupReportWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const accessGroupFields = "&fields[accessGroup]=agName,agType,agMaxCapacity,area,accessTimePattern,site";
        const areaFields = "&fields[area]=areaName";
        const siteFields = "&fields[site]=siteName";
        const accessTimePatternFields = "&fields[accessTimePattern]=atpName,atpDuration,atpMon,atpTue,atpFri,atpHol,atpSat,atpStartTime,atpEndTime,atpSun,atpThu,atpWed";
        const url = "accessGroup?include=accessTimePattern,area,site" + createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter) + accessGroupFields + areaFields + accessTimePatternFields + siteFields;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }
            new Deserializer().deserialize(
                json,
                (err: any, data: any[]) => {
                    if (err) {
                        return reject({ status: response.status, message: genericError });
                    }

                    const yes = translate ? translate.byKey(TranslationKeys.yes) : "Yes";
                    const no = translate ? translate.byKey(TranslationKeys.no) : "No";

                    const accessGroupReports: AccessGroupReport[] = data.map(
                        (accessGroupReport) => {
                            return {
                                id: accessGroupReport["id"],
                                name: accessGroupReport["ag-name"],
                                accessArea: accessGroupReport["area"]["area-name"],
                                accessTimePattern: accessGroupReport["access-time-pattern"]["atp-name"],
                                groupType: accessGroupReport['ag-type'] == 1 ? "Access Group" : "Floor Group",
                                duration: convertToDuration(accessGroupReport["access-time-pattern"]["atp-duration"]).toString(),
                                site: accessGroupReport["site"]["site-name"],
                                maxCapacity: accessGroupReport["ag-max-capacity"] ?? "-",
                                monday: accessGroupReport["access-time-pattern"]["atp-mon"] ? yes : no,
                                tuesday: accessGroupReport["access-time-pattern"]["atp-tue"] ? yes : no,
                                friday: accessGroupReport["access-time-pattern"]["atp-fri"] ? yes : no,
                                holiday: accessGroupReport["access-time-pattern"]["atp-hol"] ? yes : no,
                                saturday: accessGroupReport["access-time-pattern"]["atp-sat"] ? yes : no,
                                startTime: convertToTime(accessGroupReport["access-time-pattern"]["atp-start-time"]).toString(),
                                endTime: convertToTime(accessGroupReport["access-time-pattern"]["atp-end-time"]).toString(),
                                sunday: accessGroupReport["access-time-pattern"]["atp-sun"] ? yes : no,
                                thursday: accessGroupReport["access-time-pattern"]["atp-thu"] ? yes : no,
                                wednesday: accessGroupReport["access-time-pattern"]["atp-wed"] ? yes : no,
                            };
                        }
                    );

                    return resolve({ rows: accessGroupReports, pagination });
                }
            );
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const getAreaReport = async (
    listParameters?: ListQuery
): Promise<AreaReportWrapper> => {
    try {
        const filter: string = getFilterClause(
            listParameters?.filters,
            listParameters?.searchTerm
        );
        const includes = "include=area.site,location.zoneByZoneId,location.terminals.building,location.terminals.floor";
        const areaLocationFields = "fields[areaLocation]=location,area";
        const locationFields = "fields[location]=terminals,zoneByZoneId,locName";
        const terminalFields = "fields[terminal]=devDirection,building,floor,site,devFixedAddr";
        const buildingFields = "fields[building]=buildingName";
        const floorFields = "fields[floor]=floorName";
        const siteFields = "fields[site]=siteName";
        const areaFields = "fields[area]=areaName,totalLocations";
        const zoneFields = "fields[zone]=zoneName";
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter).replace("locName", "location.locName"); // need to add replace for column sorting
        const url = `areaLocation?${includes}&${areaLocationFields}&${locationFields}&` +
            `${terminalFields}&${buildingFields}&${floorFields}&${siteFields}&` +
            `${areaFields}&${zoneFields}` + partialURL;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }

            new Deserializer().deserialize(
                json,
                (err: any, data: any[]) => {
                    if (err) {
                        return reject({ status: response.status, message: genericError });
                    }
                    const areaReports: AreaReport[] = data.map(
                        (areaReport) => {
                            const readers = areaReport["location"]["terminals"];
                            const entryReader = readers.find(
                                (reader) => reader["dev-direction"] === 0
                            );
                            const exitReader = readers.find(
                                (reader) => reader["dev-direction"] === 1
                            );
                            const building =
                                entryReader && entryReader["building"]
                                    ? entryReader["building"]["building-name"]
                                    : exitReader && exitReader["building"]
                                        ? exitReader["building"]["building-name"]
                                        : undefined;
                            const floor =
                                entryReader && entryReader["floor"]
                                    ? entryReader["floor"]["floor-name"]
                                    : exitReader && exitReader["floor"]
                                        ? exitReader["floor"]["floor-name"]
                                        : undefined;
                            const site =
                                entryReader && entryReader["site"]
                                    ? entryReader["site"]["site-name"]
                                    : exitReader && exitReader["site"]
                                        ? exitReader["site"]["site-name"]
                                        : undefined;
                            return {
                                id: areaReport["id"],
                                name: areaReport["area"]["area-name"],
                                floor: floor,
                                site: site,
                                building: building,
                                zone:
                                    areaReport["location"]["zone-by-zone-id"][
                                    "zone-name"
                                    ],
                                location: areaReport["location"]["loc-name"],
                                entryReader: entryReader
                                    ? entryReader["dev-fixed-addr"]
                                    : "",
                                exitReader: exitReader
                                    ? exitReader["dev-fixed-addr"]
                                    : "",
                                totalLocations:
                                    areaReport["area"]["total-locations"],
                            };
                        }
                    );
                    return resolve({ rows: areaReports, pagination: pagination });
                }
            );
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const getHardwareInstallationReport = async (
    listParameters?: ListQuery
): Promise<HardwareReportWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        let partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        partialURL = partialURL.replace("hdtName", "type");
        const reportHardwareInstallationFields = "fields[reportHardwareInstallation]=address,devDesc,devName,enabled,location,tableTypeName,type,site,zone,building,floor";
        const locationFields = "fields[location]=locDesc,locName";
        const buildingFields = "fields[building]=buildingName";
        const zoneFields = "fields[zone]=zoneName";
        const floorFields = "fields[floor]=floorName";
        const siteFields = "fields[site]=siteName";
        const url = `reportHardwareInstallation?include=site,zone,location,building,floor${partialURL}&${reportHardwareInstallationFields}&${locationFields}&${buildingFields}&${zoneFields}&${floorFields}&${siteFields}`;

        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }
            new Deserializer().deserialize(
                json,
                (error: any, data: any[]) => {
                    if (error) {
                        return reject({ status: response.status, message: genericError });
                    }

                    const hadrwareInstallationReports: HardwareInstallationReport[] = data.map(
                        (report) => {
                            return {
                                id: report["id"],
                                address: report["address"],
                                deviceDescription: report["dev-desc"],
                                name: report["dev-name"],
                                enabled: report["enabled"] === 1 ? "Yes" : "No",
                                locDescription: report.location ? report.location["loc-desc"] : "",
                                locName: report.location ? report.location["loc-name"] : "",
                                tableTypeName: report["table-type-Name"],
                                type: report["type"],
                                zoneName: report.zone ? report.zone["zone-name"] : "",
                                buildingName: report.building ? report.building["building-name"] : "",
                                floorName: report.floor ? report.floor["floor-name"] : "",
                                siteName: report.site ? report.site["site-name"] : ""
                            };
                        }
                    );

                    return resolve({
                        rows: hadrwareInstallationReports,
                        pagination,
                    });
                }
            );
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const getExpiredCredentialReports = async (profileId: number, translate: Translator, listParameters?: ListQuery): Promise<ExpiredCredentialWrapper> => {
    try {
        const filter: string = getFilterClause(
            listParameters?.filters,
            listParameters?.searchTerm
        );
        const partialURL = createPartialURL(
            listParameters?.goToPage,
            listParameters?.pageSize,
            listParameters?.sortBy,
            filter
        );
        let reportFields = "fields[reportExpiredCards]=mstDisplayname,mstIdnumber,";
        reportFields += "mstSuffix,expDateDisplay,tagCode,expired,compName,deptName,";
        reportFields += "mtName,mstFirstname,mstMiddlename,mstLastname,mstTitle,";
        reportFields += "ttNameOriginal,profileName,expTime";
        const url = `reportExpiredCards?${reportFields}&${partialURL}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }
            new Deserializer().deserialize(
                json,
                (error: any, data: any[]) => {
                    if (error) {
                        return reject({ status: response.status, message: genericError });
                    }

                    const credentialReports: ExpiredCredentialReport[] = data.map(
                        (credential) => {
                            return {
                                id: credential["id"],
                                displayName: credential["mst-displayname"],
                                idNumber: credential["mst-idnumber"],
                                suffix: credential["mst-suffix"],
                                expiryDate: credential["exp-date-display"],
                                tagCode: showTagCode(
                                    profileId,
                                    credential["tag-code"]
                                ),
                                expired: credential["expired"] === 1 ? translate.byKey(TranslationKeys.yes) : translate.byKey(TranslationKeys.no),
                                companyName: credential["comp-name"],
                                departmentName: credential["dept-name"],
                                type: credential["mt-name"],
                                firstName: credential["mst-firstname"],
                                middleName: credential["mst-middlename"],
                                lastName: credential["mst-lastname"],
                                title: credential["mst-title"],
                                tagTypeName: credential["tt-trn-name"],
                                category: credential["profile-name"],
                                expiryTime: credential["exp-time"],
                            };
                        }
                    );
                    return resolve({ rows: credentialReports, pagination });
                }
            );
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

const buildPersonImages = (images?) => {
    let masterImages: PersonImage[] = [];
    if (images && images.length > 0) {
        images.map((image) => {
            masterImages.push({
                base64Image: image["base64-image"],
                base64Thumb: image["base64-thumb"],
                id: image["id"],
                masterTypeId: parseInt(image["master-image-type"]["id"]),
            });
        });
    }

    return masterImages;
};

const getGenderLabel = (gender: string, translate: Translator) => {
    if (gender === 'F') {
        return translate.byKey(TranslationKeys.female);
    }
    else if (gender === 'M') {
        return translate.byKey(TranslationKeys.male);
    }
    else {
        return translate.byKey(TranslationKeys.unspecified);
    }
}

export const getFirstInLastOutReport = async (listParameters?: ListQuery): Promise<any> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const includes = "include=master.profile,master.company,master.department";
        let url = `reportFirstInLastOut?${includes}` +
            createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);

        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);
        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }

            new Deserializer().deserialize(
                json,
                (err: any, response: any) => {
                    if (err) {
                        return reject({ status: response.status, message: genericError });
                    }
                    let newDays;
                    const firstInLastOutReport: FirstInLastOutReport[] = response.map(
                        (firstInLastOutReport, i) => {
                            const hasDepartment = (response[i].master.department != null);
                            const hasCompany = (response[i].master.company != null);
                            newDays = buildFirstInLastOutWeekDays(firstInLastOutReport)
                            return {
                                id: firstInLastOutReport["id"],
                                firstName: firstInLastOutReport['master']['mst-firstname'],
                                masterId: firstInLastOutReport['master']["id"],
                                displayName: firstInLastOutReport['master']["mst-displayname"],
                                uniqueIdentifier: firstInLastOutReport['master']['mst-idnumber'],
                                lastName: firstInLastOutReport['master']['mst-lastname'],
                                middleName: firstInLastOutReport['master']['mst-middlename'],
                                suffix: firstInLastOutReport['master']['mst-suffix'],
                                title: firstInLastOutReport['master']['mst-title'],
                                categoryType: firstInLastOutReport['master']['profile']['trn-name'],
                                profileId: firstInLastOutReport['master']['profile']['id'],
                                compName: !hasCompany ? '' : firstInLastOutReport['master']['company']['comp-name'],
                                deptName: !hasDepartment ? '' : firstInLastOutReport['master']['department']['dept-name'],
                                ...(convertArrayFirstInLastOutDaysToObject(buildFirstInLastOutWeekDays(firstInLastOutReport)))
                            };
                        }
                    );
                    return resolve({ rows: firstInLastOutReport, pagination, dynamicColumns: newDays });
                }
            );
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const getFirstInLastOutReportByReportType = async (reportType: number, listParameters?: ListQuery): Promise<any> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const filterRowType = "filter=rowType==" + `${reportType}`;
        const updatedFilters = filter.length > 0
            ? filter.replace("filter=", `${filterRowType};`)
            : `&${filterRowType}`
        const includes = "include=master.profile,master.company,master.department";
        let reportFields = "fields[reportFirstInLastOut]=master,day01,day02,day03,day04,";
        reportFields += "day05,day06,day07,day08,day09,day10,day11,day12,day13,day14,day15,";
        reportFields += "day16,day17,day18,day19,day20,day21,day22,day23,day24,day25,day26,";
        reportFields += "day27,day28,day29,day30,day31";
        let masterFields = "fields[master]=mstFirstname,mstDisplayname,mstIdnumber,";
        masterFields += "mstLastname,mstMiddlename,mstSuffix,mstTitle,profile,company,department";
        const profileFields = "fields[profile]=trnName";
        const companyFields = "fields[company]=compName";
        const departmentFields = "fields[department]=deptName";
        let url = `reportFirstInLastOut?${includes}&${reportFields}&${masterFields}&`;
        url += `${profileFields}&${companyFields}&${departmentFields}`;
        url += createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, updatedFilters);
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);
        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ message: getConstraintViolation(json.errors[0].detail) });
            }
            new Deserializer().deserialize(
                json,
                (err: any, response: any) => {
                    if (err) {
                        return reject({ status: response.status, message: genericError });
                    }
                    const firstInLastOutReport: FirstInLastOutReport[] = response.map(
                        (firstInLastOutReport, i) => {
                            const hasDepartment = (response[i].master.department != null);
                            const hasCompany = (response[i].master.company != null);
                            return {
                                id: firstInLastOutReport["id"],
                                firstName: firstInLastOutReport['master']['mst-firstname'],
                                masterId: firstInLastOutReport['master']["id"],
                                displayName: firstInLastOutReport['master']["mst-displayname"],
                                uniqueIdentifier: firstInLastOutReport['master']['mst-idnumber'],
                                lastName: firstInLastOutReport['master']['mst-lastname'],
                                middleName: firstInLastOutReport['master']['mst-middlename'],
                                suffix: firstInLastOutReport['master']['mst-suffix'],
                                title: firstInLastOutReport['master']['mst-title'],
                                categoryType: firstInLastOutReport['master']['profile']['trn-name'],
                                profileId: firstInLastOutReport['master']['profile']['id'],
                                compName: !hasCompany ? '' : firstInLastOutReport['master']['company']['comp-name'],
                                deptName: !hasDepartment ? '' : firstInLastOutReport['master']['department']['dept-name'],
                                ...(convertArrayFirstInLastOutDaysToObject(buildFirstInLastOutWeekDays(firstInLastOutReport)))
                            };
                        }
                    );
                    return resolve({ rows: firstInLastOutReport, pagination });
                }
            );
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const getFirstInLastOutReportByMasterId = async (masterId?: any, listParameters?: ListQuery): Promise<any> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const includes = "include=master.profile,master.company,master.department";
        const filterByPerson = `filter=master.id==${masterId}`;
        let url = `reportFirstInLastOut?${includes}&${filterByPerson}` +
            createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);

        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);
        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }

            new Deserializer().deserialize(
                json,
                (err: any, response: any) => {
                    if (err) {
                        return reject({ status: response.status, message: genericError });
                    }

                    const firstInLastOutReport: FirstInLastOutReport[] = response.map(
                        (firstInLastOutReport, i) => {
                            const hasDepartment = (response[i].master.department != null);
                            const hasCompany = (response[i].master.company != null);
                            return {
                                id: firstInLastOutReport["id"],
                                firstName: firstInLastOutReport['master']['mst-firstname'],
                                masterId: firstInLastOutReport['master']["id"],
                                displayName: firstInLastOutReport['master']["mst-displayname"],
                                uniqueIdentifier: firstInLastOutReport['master']['mst-idnumber'],
                                lastName: firstInLastOutReport['master']['mst-lastname'],
                                middleName: firstInLastOutReport['master']['mst-middlename'],
                                suffix: firstInLastOutReport['master']['mst-suffix'],
                                title: firstInLastOutReport['master']['mst-title'],
                                categoryType: firstInLastOutReport['master']['profile']['trn-name'],
                                profileId: firstInLastOutReport['master']['profile']['id'],
                                compName: !hasCompany ? '' : firstInLastOutReport['master']['company']['comp-name'],
                                deptName: !hasDepartment ? '' : firstInLastOutReport['master']['department']['dept-name'],
                                ...(convertArrayFirstInLastOutDaysToObject(buildFirstInLastOutWeekDays(firstInLastOutReport)))
                            };
                        }
                    );
                    return resolve({ rows: firstInLastOutReport, pagination });
                }
            );
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};


function convertArrayFirstInLastOutDaysToObject(arr) {
    const object = arr.reduce((obj, item) => ({
        ...obj,
        [item.accessor]: item.time
    }), {});

    return object;
}

export const getAbsenteeismReport = async (absentTranslatedValue: string, translate: Translator, listParameters?: ListQuery, currentWeekRpt?: boolean): Promise<AbsenteeismReportWrapper> => {
    try {
        let datesToCheckAgainst: string[] = [];
        if (currentWeekRpt) {
            datesToCheckAgainst = getAllCurrentWeekDates(new Date()).split(',');
        } else {
            datesToCheckAgainst.push(getTodayDate(true));
        }

        const dateFilter: string = `(${datesToCheckAgainst.map((date) => `absenceDates==*${date}*`).join(',')})`;
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const additionalFiltering: string = filter ? `${filter};${dateFilter}` : `&filter=${dateFilter}`;
        const reportAbsenteeismFields = "&fields[reportAbsenteeism]=mstIdnumber,mstTitle,mstDisplayname,mstFirstname,mstMiddlename,mstLastname,mstSuffix,mstCurrent,mstGender,mtName,deptName,compName,pNameOriginal,site,absenceDates";
        const siteFields = "&fields[site]=siteName";
        const url: string = "reportAbsenteeism?include=site" + createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, additionalFiltering) + reportAbsenteeismFields + siteFields;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }
            new Deserializer().deserialize(json, (err: any, data: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }

                const absenteeismRecords = data.map(absenteeism => {
                    const record: AbsenteeismReport = {
                        id: absenteeism["id"],
                        uniqueIdentifier: absenteeism["mst-idnumber"],
                        title: absenteeism["mst-title"],
                        displayName: absenteeism["mst-displayname"],
                        firstName: absenteeism["mst-firstname"],
                        middleName: absenteeism["mst-middlename"],
                        lastName: absenteeism["mst-lastname"],
                        suffix: absenteeism["mst-suffix"],
                        current: absenteeism["mst-current"] === 1 ? translate.byKey(TranslationKeys.yes) : translate.byKey(TranslationKeys.no),
                        gender: getGenderLabel(absenteeism["mst-gender"], translate),
                        mtName: absenteeism["mt-name"],
                        department: absenteeism["dept-name"],
                        company: absenteeism["comp-name"],
                        category: absenteeism["p-name-original"],
                        site: absenteeism["site"]["site-name"],
                        monday: "-",
                        tuesday: "-",
                        wednesday: "-",
                        thursday: "-",
                        friday: "-",
                        saturday: "-",
                        sunday: "-",
                    };

                    convertDatesToWeekDays(absenteeism["absence-dates"], datesToCheckAgainst).forEach((day) => {
                        record[day] = absentTranslatedValue;
                    });

                    return record;
                });

                return resolve({ rows: absenteeismRecords, pagination });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const getZoneOccupancyReport = async (profileId: number, listParameters?: ListQuery): Promise<ZoneOccupancykWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const reportZoneOccupancyFields = "&fields[reportZoneOccupancy]=site,mstDisplayname,deptName,siteName,lastTrTagcode,lastTrDateTime,mstIdnumber,departmentId,commonZoneName,commonZoneId,zoneName,zoneId,compName,companyId,mstFirstname,mstMiddlename,mstLastname,mstTitle,mstSuffix";
        const siteFields = "&fields[site]=siteName";
        const url = "reportZoneOccupancy?" + createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter) + reportZoneOccupancyFields + siteFields;
        const updatedDepartmentFilter = url.replace('department.id', 'departmentId');
        const updatedZoneFilter = updatedDepartmentFilter.replace('zone.id', 'zoneId');
        const updatedCompanyFilter = updatedZoneFilter.replace('company.id', 'companyId');

        const response = await apiConnector.fetchData(updatedCompanyFilter);
        const json = await response.json();
        const pagination = createPagination(json.meta);


        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }

            new Deserializer().deserialize(
                json,
                (err: any, data: any[]) => {
                    if (err) {
                        return reject({ status: response.status, message: genericError });
                    }

                    const zoneOccupancyReport: ZoneOccupancy[] = data.map((zoneOccupancy) => {
                        return {
                            id: zoneOccupancy["id"],
                            displayName: zoneOccupancy["mst-displayname"],
                            department: zoneOccupancy["dept-name"],
                            site: zoneOccupancy["site-name"],
                            tagCode: showTagCode(profileId, zoneOccupancy["last-tr-tagcode"]),
                            dateTime: zoneOccupancy["last-tr-date-time"].replace("T", " ").replace(".000", ""),
                            idNumber: zoneOccupancy["mst-idnumber"],
                            siteId: zoneOccupancy["site-id"],
                            departmentId: zoneOccupancy["department-id"],
                            commonZone: zoneOccupancy["common-zone-name"],
                            commonZoneId: zoneOccupancy["common-zone-id"],
                            zone: zoneOccupancy["zone-name"],
                            zoneId: zoneOccupancy["zone-id"],
                            company: zoneOccupancy["comp-name"],
                            companyId: zoneOccupancy["company-id"],
                            firstName: zoneOccupancy["mst-firstname"],
                            middleName: zoneOccupancy["mst-middlename"],
                            lastName: zoneOccupancy["mst-lastname"],
                            title: zoneOccupancy["mst-title"],
                            suffix: zoneOccupancy["mst-suffix"],
                        };
                    });
                    return resolve({ rows: zoneOccupancyReport, pagination });
                }
            );
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const getHoursWorkedReport = async (translate: Translator, listParameters?: ListQuery): Promise<HoursWorkedReportkWrapper> => {
    try {
        let filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm, 'transacksForTrMasterId.trDatetimelocal');
        const dateFilter: Filter | undefined = listParameters?.filters?.find((filter: Filter) => filter.key === 'dateOnly');
        let reportHoursWorkedFields = 'fields[reportHoursWorked]=mstDisplayname,department,site,company,profile,dateOnly,hoursWorked,hoursWorkedTillNow,';
        reportHoursWorkedFields += 'mstIdnumber,mstFirstname,mstMiddlename,mstLastname,mstTitle';
        const departmentFields = 'fields[department]=deptName';
        const companyFields = 'fields[company]=compName';
        const profileFields = 'fields[profile]=trnName';
        const siteFields = 'fields[site]=siteName';
        const include = 'include=company,site,department,profile';
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter);
        const url = `reportHoursWorked?${include}&${reportHoursWorkedFields}&${departmentFields}&${companyFields}&${profileFields}&${siteFields}${partialURL}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }
            new Deserializer().deserialize(json, (err: any, data: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }

                const hoursWorkedReport: HoursWorkedReport[] = [];

                data.forEach((hourWorkedPerson) => {
                    const personRow: HoursWorkedReport = {
                        id: hourWorkedPerson["id"],
                        displayName: hourWorkedPerson["mst-displayname"],
                        department: hourWorkedPerson['department'] ? hourWorkedPerson['department']["dept-name"] : '',
                        site: hourWorkedPerson['site']["site-name"],
                        siteId: hourWorkedPerson["site"]["id"],
                        idNumber: hourWorkedPerson["mst-idnumber"],
                        departmentId: hourWorkedPerson['department'] ? hourWorkedPerson['department']["id"] : null,
                        company: hourWorkedPerson['company'] ? hourWorkedPerson['company']["comp-name"] : '',
                        companyId: hourWorkedPerson['company'] ? hourWorkedPerson['company']["id"] : null,
                        firstName: hourWorkedPerson["mst-firstname"],
                        middleName: hourWorkedPerson["mst-middlename"],
                        lastName: hourWorkedPerson["mst-lastname"],
                        title: hourWorkedPerson["mst-title"],
                        category: hourWorkedPerson['profile'] ? hourWorkedPerson['profile']["trn-name"] : '',
                        categoryId: hourWorkedPerson['profile'] ? hourWorkedPerson['profile']["id"] : null,
                        dateOnly: "-",
                        dayOfWeek: "-",
                        hoursWorked: "-",
                        hoursWorkedNow: "-",
                    }

                    // Add the person row to the report
                    hoursWorkedReport.push(personRow);

                    // Add hours worked and totals
                    if (dateFilter) {
                        buildHoursWorkedDetails(hourWorkedPerson, translate, hoursWorkedReport, dateFilter);
                    }
                });
                return resolve({ rows: hoursWorkedReport, pagination });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const getAllTemplateReportTypeNamesWithPagination = async (listParameters?: ListQuery, userProfileId?: number): Promise<{ rows: any[]; pagination: PaginationModel }> => {
    try {
        const filter: string = getFilterClause(
            listParameters?.filters,
            listParameters?.searchTerm
        );
        const partialURL = createPartialURL(
            listParameters?.goToPage,
            listParameters?.pageSize,
            listParameters?.sortBy,
            filter
        );
        let url = `report?include=reportType,reportSubType&sort=rptSysname,reportType.trnName,trnName${partialURL}`;

        if ((userProfileId !== ROLES.ID_SYSTEM_OWNER) && (userProfileId !== ROLES.ID_SITE_ADMIN) && (userProfileId !== ROLES.ID_INSTALLER)) {
            // hide general mustering report from unsupported roles
            const filterOutReport = `reportType.id=out=(${ReportTypes.MUSTERING})`;
            url = url + ';' + filterOutReport;
        }

        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }
            new Deserializer().deserialize(
                json,
                (error: any, data: any[]) => {
                    if (error) {
                        return reject({ status: response.status, message: genericError });
                    }

                    const reportTypes: any[] = data.map(
                        (reportType): any => {
                            return {
                                value: reportType["id"],
                                label: reportType["trn-name"],
                                reportType: reportType["report-type"]["id"],
                                reportSubType: reportType["report-sub-type"] ? reportType["report-sub-type"]["id"] : null,
                            };
                        }
                    );

                    return resolve({ rows: reportTypes, pagination: pagination });
                }
            );
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject({ message: genericError })
        });
    }
};

export const getMusteringReport = async (translate: Translator, listParameters?: ListQuery): Promise<MusteringReportReportWrapper> => {
    try {
        const filter: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        const reportGeneralMusteringFields = "&fields[reportGeneralMustering]=mstIdnumber,mstDisplayname,mstFirstname,mstLastname,mstSuffix,mobileNumber,deviceName,site,locName,time,status,department,company,profile";
        const departmentFields = "&fields[department]=deptName";
        const companyFields = "&fields[company]=compName";
        const profileFields = "&fields[profile]=trnName";
        const siteFields = "&fields[site]=siteName";
        const url = "reportGeneralMustering?include=department,company,site,profile" + createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filter) + reportGeneralMusteringFields + departmentFields + companyFields + profileFields + siteFields;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }

            new Deserializer().deserialize(json, (err: any, response: any) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }

                const musteringReportData: MusteringReport[] = response.map((data) => {
                    return {
                        id: data["id"],
                        idNumber: data["mst-idnumber"],
                        displayName: data["mst-displayname"],
                        status: getStatusDescription(data["status"], translate),
                        lastLocation: data["loc-name"],
                        lastSeenTime: data["time"],
                        department: data["department"] ? data["department"]["dept-name"] : "",
                        company: data["company"] ? data["company"]["comp-name"] : "",
                        firstName: data["mst-firstname"],
                        lastName: data["mst-lastname"],
                        suffix: data["mst-suffix"],
                        contactNumber: data["mobile-number"] ? data["mobile-number"] : "",
                        site: data["site"] ? data["site"]["site-name"] : "",
                        profile: data["profile"] ? data["profile"]["trn-name"] : "",
                        deviceName: data["device-name"] ? data["device-name"] : "",
                        statusValue: data["status"],
                    }
                });

                return resolve({ rows: musteringReportData, pagination: pagination });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

const getStatusDescription = (status: number, translate: Translator) => {
    switch (status) {
        case 1:
            return getMusterReportStatus("ABSENT", translate);
        case 2:
            return getMusterReportStatus("MUSTERED", translate);
        case 3:
            return getMusterReportStatus("MANUALLY_MUSTERED", translate);
        case 4:
            return getMusterReportStatus("UNACCOUNTED", translate);
        case 5:
            return getMusterReportStatus("EXITED", translate);
        default:
            return getMusterReportStatus("ABSENT", translate);
    }
};

export const getMusteringReportTotals = async (siteId: string, reportId: number): Promise<MusteringReportTotals> => {
    try {
        const url = '';
        const headers = {
            type: "generalMusteringReportTotal",
            siteId: siteId,
            reportId: reportId,
        }
        const response = await apiConnector.fetchData(url, true, headers);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: genericError });
            }

            const totals: MusteringReportTotals = {
                absent: json["absent"],
                exited: json["exited"],
                manuallyMustered: json["manuallyMustered"],
                mustered: json["mustered"],
                totalMustered: json["totalMustered"],
                totalOnSite: json["totalOnSite"],
                unaccounted: json["unaccounted"],
                unknown: json["unknown"],
                unaccountedWithExited: (json["unaccounted"] + json["exited"])
            }

            return resolve(totals);
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getStillOnSiteReport = async (translate: Translator, warn: ToastFunction, listParameters?: ListQuery): Promise<StillOnSiteWrapper> => {
    try {
        const siteExitFilter = listParameters?.filters?.find(filter => filter.key === "transacksForTrMasterId.terminal.id");
        if (!!!siteExitFilter || siteExitFilter.selected.length < 0 || siteExitFilter.selected.filter(selected => selected.ignore !== true).length <= 0) {
            return new Promise((resolve, reject) => {
                warn(translate.byKey(TranslationKeys.siteExitMessage));
                resolve({ rows: [], pagination: { maxPageSize: 0, pageNumber: 0, totalCount: 0, totalPages: 0 } });
            });
        }

        const include: string = getIncludeQueryString(listParameters?.filters ?? []);
        let masterFields = "fields[master]=mstDisplayname,profile,department,company,";
        masterFields += "mstFirstname,mstLastname,mstMiddlename,mstEmailAddress,mstSuffix,";
        masterFields += "mstTitle,mstIdnumber,mstMobileAddress,site,transacksForTrMasterId";
        const profileFields = "fields[profile]=trnName";
        const departmentFields = "fields[department]=deptName";
        const companyFields = "fields[company]=compName";
        let transactionFields = "fields[transack]=trDatetimelocal,trDevName,";
        transactionFields += "eventType,terminal";
        const eventTypeFields = "fields[eventType]=etName";
        let url = `master?${include}&${masterFields}&${profileFields}&`;
        url += `${departmentFields}&${companyFields}&${transactionFields}&` + `${eventTypeFields}`;
        url += createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy);
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }

            new Deserializer().deserialize(json, (err: any, masters: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError });
                }
                const filteredMasters = getFilteredMasters(masters, listParameters?.filters ?? [], listParameters?.searchTerm);
                const masterStillOnSite: StillOnSite[] = filteredMasters.map((master): StillOnSite => {
                    const thisDateTransactions = master['transacks-for-tr-master-id'].sort((a, b) => b['tr-datetimelocal'].localeCompare(a['tr-datetimelocal']));

                    return {
                        id: master["id"],
                        name: master["mst-displayname"],
                        category: master['profile']['trn-name'],
                        deptName: master['department'] ? master['department']['dept-name'] : '',
                        compName: master['company'] ? master['company']['comp-name'] : '',
                        firstName: master['mst-firstname'],
                        lastName: master['mst-lastname'],
                        middleName: master['mst-middlename'],
                        firstIn: thisDateTransactions.length > 0 ? getFormattedTime(getTime(new Date(thisDateTransactions[thisDateTransactions.length - 1]['tr-datetimelocal']))) : '',
                        lastLocation: thisDateTransactions.length > 0 ? thisDateTransactions[0]['tr-dev-name'] : '',
                        lastSeen: thisDateTransactions.length > 0 ? getFormattedTime(getTime(new Date(thisDateTransactions[0]['tr-datetimelocal']))) : '',
                        email: master["mst-email-address"],
                        suffix: master["mst-suffix"],
                        title: master["mst-title"],
                        uid: master["mst-idnumber"],
                        mobileNumber: master["mst-mobile-address"],

                    }
                });

                return resolve({ rows: masterStillOnSite, pagination });
            });
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

const getIncludeQueryString = (filters: Filter[]) => {
    const dateFilter = filters.find((filter) => filter.key === 'dateOnly');

    if (dateFilter && dateFilter.selected.length > 0) {
        return `include=profile,company,department,site,transacksForTrMasterId.terminal,transacksForTrMasterId.eventType&filter[transack]=trDatetimeutc=='${dateFilter.selected[0].value}*'`;
    }

    return '';
}

const getFilteredMasters = (masters: any[], filters: Filter[], searchTerm?: SearchTerm) => {
    const siteExitFilter = filters.find(filter => filter.key === "transacksForTrMasterId.terminal.id");
    const siteFilter = filters.find(filter => filter.key === "site.id");
    const departmentFilter = filters.find(filter => filter.key === "department.id");
    const companyFilter = filters.find(filter => filter.key === "company.id");
    const profileFilter = filters.find(filter => filter.key === "profile.id");
    const timeFilter = filters.find(filter => filter.key === "time");
    let filteredMasters: any[] = [];

    if (siteFilter && siteFilter.selected.length > 0) {
        const siteId = siteFilter.selected[0].value;
        filteredMasters = masters.filter((master) => master['site']['id'] === siteId);
    }

    if (searchTerm && searchTerm.value) {
        const accessor = searchTerm.accessor.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
        filteredMasters = filteredMasters.filter((master) => {
            if (searchTerm.accessor === 'company.compName' && master['company']) {
                return master['company']['comp-name'].includes(searchTerm.value);
            }
            else if (searchTerm.accessor === 'department.deptName' && master['department']) {
                return master['department']['dept-name'].includes(searchTerm.value);
            }
            else if (searchTerm.accessor === 'profile.trnName' && master['profile']) {
                return master['profile']['trn-name'].includes(searchTerm.value);
            }

            return master[accessor] && master[accessor].includes(searchTerm.value);
        });
    }

    if (siteExitFilter && siteExitFilter.selected.length > 0 && timeFilter && timeFilter.selected.length > 0) {
        const siteExitIds = siteExitFilter.selected.map(selected => selected.value);

        filteredMasters = filteredMasters.filter((master) => {
            const masterTransacks: any[] = master['transacks-for-tr-master-id'];
            const onlyAllowedTransactions = masterTransacks.filter((transack) => transack['event-type']['et-name'].includes('EV_Allowed'));
            if (onlyAllowedTransactions && onlyAllowedTransactions.length > 0) {
                const lastMasterTransack = onlyAllowedTransactions[onlyAllowedTransactions.length - 1];
                const lastMasterTransackTime = getFormattedTime(getTime(new Date(lastMasterTransack['tr-datetimelocal'])));

                if (lastMasterTransackTime.localeCompare(timeFilter.selected[0].value) >= 0) {
                    return true;
                }
                else if (lastMasterTransack['terminal'] && !siteExitIds.includes(lastMasterTransack['terminal']['id'])) {
                    return true;
                }
            }

            return false;
        });
    }

    if (departmentFilter && departmentFilter.selected.length > 0) {
        const departmentIds = departmentFilter.selected.map(selected => selected.value);;
        filteredMasters = filteredMasters.filter((master) => {
            return master['department'] && departmentIds.includes(master['department']['id'])
        });
    }

    if (companyFilter && companyFilter.selected.length > 0) {
        const companyIds = companyFilter.selected.map(selected => selected.value);
        filteredMasters = filteredMasters.filter((master) => {
            return master['company'] && companyIds.includes(master['company']['id'])
        });
    }

    if (profileFilter && profileFilter.selected.length > 0) {
        const profileIds = profileFilter.selected.map(selected => selected.value);
        filteredMasters = filteredMasters.filter((master) => profileIds.includes(master['profile']['id']));
    }

    return filteredMasters;
}

export const getVingcardTransactionReport = async (listParameters?: ListQuery): Promise<VingcardTransactionReportWrapper> => {
    try {
        const newFilters: Filter[] = [];
        listParameters?.filters?.forEach((filter) => {
            if (filter.key === 'dateOnly') {
                const selected = filter.selected.map((selected) => {
                    return {
                        ...selected,
                        value: getUnFormattedDate(selected.value)
                    }
                });

                newFilters.push({ ...filter, selected });
                return;
            }

            newFilters.push(filter);
        })

        const filter: string = getFilterClause(
            newFilters,
            listParameters?.searchTerm
        );
        const partialURL = createPartialURL(
            listParameters?.goToPage,
            listParameters?.pageSize,
            listParameters?.sortBy,
            filter
        );

        const url = `vingCardTransaction?${partialURL}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }
            new Deserializer().deserialize(
                json,
                (error: any, data: any[]) => {
                    if (error) {
                        return reject({ status: response.status, message: genericError });
                    }

                    const transactions: VingcardTransactionReport[] = data.map((report) => {
                        return {
                            id: report["id"],
                            name: "",
                            eventTime: getFormattedDateTime(report["event-time"]),
                            dateOnly: getFormattedDate(report["date-only"]),
                            timeOnly: getFormattedTimeFromNum(report["time-only"]),
                            registrationNumber: report["registration-number"],
                            cardNameID: report["card-name-id"],
                            doorId: report["door-id"],
                            eventCode: report["event-code"],
                            eventDescription: report["event-discription"],
                        };
                    });
                    return resolve({ rows: transactions, pagination });
                }
            );
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};